import React, { useRef, useEffect } from 'react';
import { HistoricalEvent } from '../../services/timeTunnelService';
import './TimeTunnel.css';

interface LocationComparisonProps {
  events: HistoricalEvent[];
  selectedYear: number | null;
  selectedMonth: number | null;
  selectedDay: number | null;
  onEventClick: (event: HistoricalEvent) => void;
}

const LocationComparison: React.FC<LocationComparisonProps> = ({
  events,
  selectedYear,
  selectedMonth,
  selectedDay,
  onEventClick
}) => {
  const scrollRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Group events by country
  const groupEventsByCountry = () => {
    const grouped: { [country: string]: HistoricalEvent[] } = {};
    events.forEach(event => {
      if (!grouped[event.country]) {
        grouped[event.country] = [];
      }
      grouped[event.country].push(event);
    });
    
    // Sort events within each country by date
    Object.keys(grouped).forEach(country => {
      grouped[country].sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        if ((a.month || 0) !== (b.month || 0)) return (a.month || 0) - (b.month || 0);
        return (a.day || 0) - (b.day || 0);
      });
    });
    
    return grouped;
  };

  const eventsByCountry = groupEventsByCountry();
  const countries = Object.keys(eventsByCountry).sort();

  // Generate a color for each country
  const getCountryColor = (country: string, index: number) => {
    const hue = (index * 137.5) % 360; // Golden angle for distribution
    return `hsl(${hue}, 45%, 45%)`;
  };

  const formatDate = (event: HistoricalEvent) => {
    const parts: string[] = [];
    if (event.year) parts.push(event.year.toString());
    if (event.month) {
      const monthName = new Date(2000, event.month - 1).toLocaleString('default', { month: 'short' });
      parts.push(monthName);
    }
    if (event.day) parts.push(event.day.toString());
    return parts.join(' ');
  };

  const getFilterDescription = () => {
    const parts: string[] = [];
    if (selectedYear) parts.push(`${selectedYear}`);
    if (selectedMonth) {
      const monthName = new Date(2000, selectedMonth - 1).toLocaleString('default', { month: 'long' });
      parts.push(monthName);
    }
    if (selectedDay) parts.push(`Day ${selectedDay}`);
    return parts.length > 0 ? parts.join(', ') : 'All time';
  };

  // Synchronized scrolling
  const handleScroll = (country: string) => {
    const scrollTop = scrollRefs.current[country]?.scrollTop || 0;
    Object.keys(scrollRefs.current).forEach(key => {
      if (key !== country && scrollRefs.current[key]) {
        scrollRefs.current[key]!.scrollTop = scrollTop;
      }
    });
  };

  if (events.length === 0) {
    return (
      <div className="location-comparison-container">
        <div className="location-comparison-header">
          <h3>Location Comparison</h3>
          <span className="location-comparison-filter">{getFilterDescription()}</span>
        </div>
        <div className="location-comparison-empty">
          <p>No events found to compare.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="location-comparison-container">
      <div className="location-comparison-header">
        <h3>Location Comparison</h3>
        <span className="location-comparison-filter">{getFilterDescription()}</span>
      </div>
      <div className="location-comparison-grid">
        {countries.map((country, index) => (
          <div key={country} className="location-comparison-column">
            <div 
              className="location-comparison-country-header"
              style={{ borderTopColor: getCountryColor(country, index) }}
            >
              <div 
                className="location-comparison-country-dot"
                style={{ backgroundColor: getCountryColor(country, index) }}
              />
              <span className="location-comparison-country-name">{country}</span>
              <span className="location-comparison-country-count">
                {eventsByCountry[country].length}
              </span>
            </div>
            <div 
              className="location-comparison-events"
              ref={el => scrollRefs.current[country] = el}
              onScroll={() => handleScroll(country)}
            >
              {eventsByCountry[country].map(event => (
                <div 
                  key={event.id} 
                  className="location-comparison-event"
                  onClick={() => onEventClick(event)}
                >
                  <div className="location-comparison-event-date">
                    {formatDate(event)}
                  </div>
                  <div className="location-comparison-event-title">
                    {event.title}
                  </div>
                  {event.description && (
                    <div className="location-comparison-event-description">
                      {event.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocationComparison;

